import { memo, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TransactionV2 } from '../../../types/dashboardV2';
import { ocppService } from '../../../services/ocpp/OcppService';
import { formatTransactionStatus, getTransactionStatusBadgeClass, getTransactionStatusDotClass } from '../../../shared/utils/statusUtils';
import { CopyIdButton } from '../../../components/common/CopyIdButton/CopyIdButton';

interface TransactionDetailsModalProps {
  isOpen: boolean;
  transaction: TransactionV2 | null;
  onClose: () => void;
}

export const TransactionDetailsModal = memo(function TransactionDetailsModal({
  isOpen,
  transaction,
  onClose,
}: TransactionDetailsModalProps) {
  const { t } = useTranslation();
  const [invoiceDetails, setInvoiceDetails] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const fetchDetails = useCallback(async () => {
    if (!transaction?.invoiceId) return;

    setLoading(true);
    setError(null);
    try {
      const details = await ocppService.getInvoiceStatus(transaction.invoiceId);
      setInvoiceDetails(details);
    } catch (err) {
      setError(t('dashboard.transactions.modalFetchFailed'));
      console.error('Failed to fetch invoice details:', err);
    } finally {
      setLoading(false);
    }
  }, [transaction?.invoiceId, t]);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus trap and initial focus
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    // Focus the close button when modal opens
    closeButtonRef.current?.focus();

    const modal = modalRef.current;
    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    modal.addEventListener('keydown', handleTabKey);
    return () => modal.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && transaction?.invoiceId) {
      fetchDetails();
    }
    return () => {
      setInvoiceDetails(null);
      setError(null);
    };
  }, [isOpen, transaction?.invoiceId, fetchDetails]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen || !transaction) return null;

  return (
    <>
      {/* Overlay */}
      <div className="modal-overlay" onClick={onClose} aria-hidden="true" />

      {/* Modal */}
      <div
        ref={modalRef}
        className="modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="modal-header">
          <h2 id="modal-title" className="modal-title">{t('dashboard.transactions.modalTitle')}</h2>
          <button
            ref={closeButtonRef}
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('dashboard.transactions.modalCloseAria')}
          >
            <span className="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </div>

        <div className="modal-content">
          {/* Transaction Summary */}
          <div className="modal-section">
            <div className="modal-summary">
              <div className="modal-summary-row">
                <span className="modal-label">{t('dashboard.transactions.modalLabelTranId')}</span>
                <span className="modal-value">{transaction.tranId || '-'}</span>
              </div>
              <div className="modal-summary-row">
                <span className="modal-label">{t('dashboard.transactions.modalLabelStatus')}</span>
                <span className={`status-badge ${getTransactionStatusBadgeClass(transaction.status)}`}>
                  <span className={`status-dot ${getTransactionStatusDotClass(transaction.status)}`} />
                  {formatTransactionStatus(transaction.status)}
                </span>
              </div>
              <div className="modal-summary-row">
                <span className="modal-label">{t('dashboard.transactions.modalLabelAmount')}</span>
                <span className="modal-value modal-value-highlight">{transaction.amount.toFixed(2)} ₴</span>
              </div>
              <div className="modal-summary-row">
                <span className="modal-label">{t('dashboard.transactions.modalLabelBank')}</span>
                <span className="modal-value">{transaction.bank || '-'}</span>
              </div>
              <div className="modal-summary-row">
                <span className="modal-label">{t('dashboard.transactions.modalLabelUserId')}</span>
                <span className="modal-value modal-value-small modal-value-copy">
                  <span className="modal-value-id" title={transaction.userId || ''}>
                    {transaction.userId || '-'}
                  </span>
                  {transaction.userId && (
                    <CopyIdButton value={transaction.userId} label={t('dashboard.tables.sessions.copyUserId')} variant="icon" />
                  )}
                </span>
              </div>
              <div className="modal-summary-row">
                <span className="modal-label">{t('dashboard.transactions.modalLabelInvoiceId')}</span>
                <span className="modal-value modal-value-small">{transaction.invoiceId || '-'}</span>
              </div>
              <div className="modal-summary-row">
                <span className="modal-label">{t('dashboard.transactions.modalLabelDate')}</span>
                <span className="modal-value">{formatDate(transaction.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          {transaction.invoiceId && (
            <div className="modal-section">
              <h3 className="modal-section-title">{t('dashboard.transactions.modalInvoiceDetails')}</h3>
              {loading ? (
                <div className="modal-loading">
                  <div className="loading-spinner" />
                  <span>{t('dashboard.transactions.modalLoadingInvoice')}</span>
                </div>
              ) : error ? (
                <div className="modal-error" role="alert">
                  <span className="material-symbols-outlined" aria-hidden="true">error</span>
                  <span>{error}</span>
                </div>
              ) : invoiceDetails ? (
                <pre className="modal-code">
                  {JSON.stringify(invoiceDetails, null, 2)}
                </pre>
              ) : null}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="modal-btn-secondary" onClick={onClose}>
            {t('dashboard.transactions.modalClose')}
          </button>
        </div>
      </div>
    </>
  );
});

export default TransactionDetailsModal;
